// Module: MultiImageUploadZone
// Role: Presentation-only upload zone for one MultiImageUploadBrick requirement.
// Uses: multi-image-requirements:descriptionFor,labelFor; styles/tokens; atoms/Ic
// Used by: MultiImageUploadBrick.tsx

import { Ic } from '../../../components/atoms/Ic';
import { P, S, T } from '../../../styles/tokens';
import type { ArtefactRequirement } from '../../../api/flow-types';
import { descriptionFor, labelFor } from './multi-image-requirements';

const SUCCESS_SOFT_BG = '#EFF7E9';

export interface MultiImageUploadZoneProps {
  req: ArtefactRequirement;
  index: number;
  thumbUrl: string | null;
  done: boolean;
  busy: boolean;
  disabled: boolean;
  pickable: boolean;
  onPick: (index: number) => void;
}

export function MultiImageUploadZone({
  req,
  index,
  thumbUrl: thumb,
  done,
  busy,
  disabled,
  pickable,
  onPick,
}: MultiImageUploadZoneProps) {
  return (
    <button
      type="button"
      onClick={() => onPick(index)}
      disabled={disabled}
      data-testid={`multi-upload-zone-${index}`}
      style={{
        width: '100%',
        borderRadius: 16,
        border: `2px solid ${done ? P.success : P.terracotta}`,
        // Заливка зелёным только в пустом/загруженном-без-превью виде.
        // Под самим превью фон тёмный, иначе objectFit:cover не нужен,
        // а contain давал большие зелёные поля по бокам вертикальных
        // скриншотов («зелёное огромное окно»).
        background: thumb ? '#1C1B1A' : done ? SUCCESS_SOFT_BG : P.terraSoft,
        padding: thumb ? 0 : '14px 12px',
        minHeight: thumb ? 132 : 112,
        maxHeight: thumb ? 132 : undefined,
        cursor: pickable ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        overflow: 'hidden',
        textAlign: 'left',
        position: 'relative',
      }}
    >
      {thumb ? (
        <>
          <img
            src={thumb}
            alt={labelFor(req, index)}
            style={{ width: '100%', height: 132, objectFit: 'cover', display: 'block' }}
          />
          {busy && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0,0,0,0.42)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
              data-testid={`multi-upload-busy-${index}`}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: '3px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#fff',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <div style={{ font: T.caption, fontWeight: 600, color: '#fff' }}>
                Загружаем…
              </div>
            </div>
          )}
          {done && (
            <div
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: P.success,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Ic name="check" size={16} color="#fff" />
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Ic name="camera" size={24} color={P.terracotta} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: T.subhead, fontWeight: 700, color: S.label }}>
                {labelFor(req, index)}
              </div>
              <div
                style={{
                  font: T.captionAlt,
                  color: S.secondaryLabel,
                  marginTop: 3,
                  lineHeight: 1.35,
                }}
              >
                {descriptionFor(req)}
              </div>
            </div>
          </div>
        </>
      )}
    </button>
  );
}
